import { IconButton, Modal, Tooltip, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useApiError from 'packages/novous_web/src/api/useApiError';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import AlertToast from 'packages/novous_web/src/common/components/Alert';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import CustomMultiSelectDropDown from 'packages/novous_web/src/common/components/CustomMultiSelectDropDownInput';
import InputField from 'packages/novous_web/src/common/components/InputField';
import React, { useEffect, useMemo, useState } from 'react';
import removeIcon from 'packages/novous_web/src/assets/icons/close.svg';
import DateField from 'packages/novous_web/src/common/components/DateField';

interface ModifySubscriptionProps {
  open: boolean;
  onClose: () => void;
  subscription: any;
  onSubscriptionUpdated?: () => void;
}

const formatDate = (date: Date) => {
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const filterDevicesByType = (devices: any[], selectedDeviceType: string) => {
  if (!selectedDeviceType || !devices) return devices;

  switch (selectedDeviceType) {
    case 'NOVUS LITE':
      return devices.filter((device) => device.camCount <= 2 && device.dataSource === 'vp2');

    case 'NOVUS LITE MAX':
      return devices.filter((device) => device.camCount > 2 && device.dataSource === 'vp2');

    case 'NOVUS PRO':
      return devices.filter((device) => device.camCount <= 2 && device.dataSource !== 'vp2');

    case 'NOVUS PRO MAX':
      return devices.filter((device) => device.camCount > 2 && device.dataSource !== 'vp2');

    default:
      return devices;
  }
};

const ModifySubscription: React.FC<ModifySubscriptionProps> = ({
  open,
  onClose,
  subscription,
  onSubscriptionUpdated,
}) => {
  console.log(subscription, 'subscription');
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });
  const [isEndSubscriptionModalOpen, setIsEndSubscriptionModalOpen] = useState(false);
  const { user } = useAuth();
  const [originalDeviceIds, setOriginalDeviceIds] = useState<string[]>([]);
  const [originalPriceDue, setOriginalPriceDue] = useState<string>('');
  const [formData, setFormData] = useState<{
    selectDevices: string[];
    priceDue: string;
    refrenceId: string;
    invoiceDate: string | null;
    selectedDevice: string[];
  }>({
    selectDevices: [],
    priceDue: '',
    refrenceId: '',
    invoiceDate: null,
    selectedDevice: [],
  });
  // const [formData, setFormData] = React.useState({
  //     selectDevices: [] as string[],
  //     priceDue: '',
  //     refrenceId: '',
  //     invoiceDate: '',
  //     selectedDevice: [] as string[]
  // });
  const [newSelectedDevices, setNewSelectedDevices] = React.useState<string[]>([]);
  const [filteredDevices, setFilteredDevices] = useState<any[]>([]);
  const [isInternal, setIsInternal] = useState<boolean>(false);
  useEffect(() => {
    const currentPath = window.location.pathname;
    const isInternalSubscription = currentPath.includes('internal-subscriptions');
    setIsInternal(isInternalSubscription);
  }, []);
  if (!open) return null;
  let sortKeyDate: Date | null = null;
  let validTillDate: Date | null = null;
  let daysRemaining: number | null = null;

  try {
    const sortKeyParts = subscription.sortKey?.split('#');
    const sortKeyDateRaw = sortKeyParts?.[1];

    if (sortKeyDateRaw) {
      const [datePart, timePart] = sortKeyDateRaw.split(':');
      const timeParts = sortKeyDateRaw.substring(11);
      const sortKeyISO = `${datePart}T${timeParts}`;
      sortKeyDate = new Date(sortKeyISO);
    }

    if (subscription.validTill) {
      validTillDate = new Date(subscription.validTill.replace(' ', 'T'));
    }

    if (sortKeyDate && validTillDate) {
      const diffMs = validTillDate.getTime() - sortKeyDate.getTime();
      daysRemaining = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    }
  } catch (error) {
    console.error('Date parsing error:', error);
  }

  const {
    data: companyListByCrnDevices,
    error: companyListByCrnDevicesError,
    loading: companyListByCrnDevicesLoading,
    fetchData: fetchCompanyListByCrnDevicesData,
  } = useAxios<any>(`${API_ENDPOINTS.users.CUSTOMER_LIST_DEVICES}`, 'POST');

  useEffect(() => {
    fetchCompanyListByCrnDevicesData({
      fleetId: subscription?.fleetId,
    });
  }, []);

  useEffect(() => {
    if (companyListByCrnDevices?.data && subscription?.deviceType) {
      const filtered = filterDevicesByType(companyListByCrnDevices.data, subscription.deviceType);
      setFilteredDevices(filtered);

      const currentSelected = newSelectedDevices;
      const validSelected = currentSelected.filter((id) => filtered.some((device) => device.deviceId === id));

      if (validSelected.length !== currentSelected.length) {
        setNewSelectedDevices(validSelected);
        setToast({
          open: true,
          message: `Some devices were removed as they don't match the ${subscription.deviceType} device type`,
          type: 'warning',
        });
      }
    } else {
      setFilteredDevices([]);
    }
  }, [companyListByCrnDevices?.data, subscription?.deviceType, newSelectedDevices]);

  useEffect(() => {
    if (subscription?.deviceIds?.length > 0) {
      if (companyListByCrnDevices?.data && subscription?.deviceType) {
        const filtered = filterDevicesByType(companyListByCrnDevices.data, subscription.deviceType);
        const validDeviceIds = subscription.deviceIds.filter((id: string) =>
          filtered.some((device) => device.deviceId === id),
        );
        setNewSelectedDevices(validDeviceIds);
      } else {
        setNewSelectedDevices([...subscription.deviceIds]);
      }
    }
  }, [subscription, companyListByCrnDevices?.data]);

  const handleDeviceChange = (value: string, index: number) => {
    if (newSelectedDevices.includes(value) && newSelectedDevices[index] !== value) {
      setToast({
        open: true,
        message: `Device ${value} is already selected. Please choose a different device.`,
        type: 'error',
      });
      return;
    }
    const updatedDevices = [...newSelectedDevices];
    updatedDevices[index] = value;
    setNewSelectedDevices(updatedDevices);
  };

  const {
    data: updateSubscriptionData,
    error: updateSubscriptionError,
    loading: updateSubscriptionLoading,
    fetchData: updateSubscriptionRequestData,
  } = useApiError<any>(API_ENDPOINTS.clientSubscription.UPDATE_SUBSCRIPTION, 'POST');

  const {
    data: endSubscriptionData,
    error: endSubscriptionError,
    loading: endSubscriptionLoading,
    fetchData: endSubscriptionRequestData,
  } = useApiError<any>(API_ENDPOINTS.clientSubscription.END_SUBSCRIPTION, 'POST');

  const checkDeviceQuantityValidation = (devices: string[]): boolean => {
    const quantity = subscription?.quantity || 0;
    if (devices.length > quantity) {
      setToast({
        open: true,
        message: `You cannot add more than ${quantity} devices. Current quantity is ${quantity} but you have selected ${devices.length} devices.`,
        type: 'error',
      });
      return false;
    }
    return true;
  };

  const updateSubscription = async () => {
    if (!checkDeviceQuantityValidation(newSelectedDevices)) {
      return;
    }
    const payload = {
      fleetId: subscription?.fleetId,
      deviceIds: newSelectedDevices,
      subscriptionCode: subscription?.subscriptionCode,
      pricePaid: formData.priceDue,
      referenceNumber: formData.refrenceId,
      referenceDate: formData.invoiceDate,
      channelType: isInternal,
    };

    console.log(payload, 'payload');

    try {
      const response = await updateSubscriptionRequestData(payload);
      if (response?.status === 200 || response?.status === 201) {
        console.log(response.message, 'updateSubscription');
        setToast({ open: true, message: response.message, type: 'success' });
        setTimeout(() => {
          if (onSubscriptionUpdated) onSubscriptionUpdated();
          onClose();
        }, 500);
      } else {
        setToast({ open: true, message: response.message, type: 'error' });
      }
    } catch (error: any) {
      setToast({
        open: true,
        message: 'Error Occured while creating subscription',
        type: 'error',
      });
    }
  };

  const handleEndSubscription = async () => {
    const payload = {
      fleetId: subscription?.fleetId,
      subscriptionCode: subscription?.subscriptionCode,
      status: 'terminated',
      channelType: isInternal,
    };

    try {
      const response = await endSubscriptionRequestData(payload);
      if (response?.status === 200 || response?.status === 201) {
        setToast({ open: true, message: response.message, type: 'success' });
        setTimeout(() => {
          if (onSubscriptionUpdated) onSubscriptionUpdated();
          onClose();
        }, 500);
      } else {
        setToast({ open: true, message: response.message, type: 'error' });
      }
    } catch (error: any) {
      setToast({ open: true, message: 'Error Occured while creating subscription', type: 'error' });
    }
  };

  const availableDevices = useMemo(() => {
    return filteredDevices
      .filter((device: any) => !newSelectedDevices.includes(device.deviceId))
      .map((device: any) => ({
        value: device.deviceId,
        label: `${device.deviceId}`,
      }));
  }, [filteredDevices, newSelectedDevices]);

  const value = subscription.status?.toLowerCase() || 'na';

  const statusStyles: Record<string, { bg: string; color: string }> = {
    active: { bg: '#D9EDE6', color: '#219653' },
    'expiring soon': { bg: '#FFF4E5', color: '#F2994A' },
    expired: { bg: '#FDEDED', color: '#EB5757' },
    terminated: { bg: '#E0E0E0', color: '#4F4F4F' },
    na: { bg: '#F2F2F2', color: '#828282' },
  };

  const { bg, color } = statusStyles[value] || statusStyles['na'];

  useEffect(() => {
    if (subscription?.deviceIds?.length > 0) {
      setNewSelectedDevices([...subscription.deviceIds]);
      setOriginalDeviceIds([...subscription.deviceIds]);
    }

    if (subscription?.priceDue) {
      setFormData((prev) => ({ ...prev, priceDue: subscription.priceDue }));
      setOriginalPriceDue(subscription.priceDue);
    }
  }, [subscription]);

  const hasChanges = useMemo(() => {
    const priceChanged = formData.priceDue !== originalPriceDue;

    const devicesChanged =
      originalDeviceIds.length !== newSelectedDevices.length ||
      originalDeviceIds.some((id, idx) => id !== newSelectedDevices[idx]);

    const hasAtLeastOneDevice = newSelectedDevices.length > 0;

    return hasAtLeastOneDevice && (priceChanged || devicesChanged);
  }, [formData.priceDue, originalPriceDue, newSelectedDevices, originalDeviceIds]);

  const handleReferenceIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[a-zA-Z0-9]*$/.test(value)) {
      setFormData({ ...formData, refrenceId: value });
    }
  };

  return (
    <>
      <AlertToast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        type={toast.type as any}
      />
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            maxHeight: '90vh',
            overflowY: 'auto',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '1000px',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 4,
          }}
        >
          <Box className="flex justify-between items-center w-full">
            <Typography variant="h6" sx={{ color: (theme) => theme.palette.text.primary, fontWeight: 600 }}>
              Modify Subscription
            </Typography>
            <Box className="flex justify-end" sx={{ mt: 2, gap: 2 }}>
              <Box className="flex flex-col gap-2">
                <CustomIconButton
                  text="End Subscription"
                  onClick={() => setIsEndSubscriptionModalOpen(true)}
                  variant="outlined"
                  background="transparent"
                  color="error"
                  outline={false}
                  padding="0"
                />
              </Box>
            </Box>
          </Box>
          <Box
            sx={(theme) => ({
              mt: 2,
              borderColor: theme.palette.selected,
              borderWidth: 1,
              padding: 2,
              borderRadius: 4,
            })}
          >
            <Box className="grid lg:grid-cols-4 md:grid-cols-4 grid-cols-2" sx={{ mt: 2, gap: 2 }}>
              <Box className="flex flex-col gap-2">
                <Typography variant="body2" fontWeight={400} fontSize={14}>
                  Subscription Code
                </Typography>
                <Typography variant="body2" fontWeight={600} fontSize={14}>
                  {subscription?.subscriptionCode}
                </Typography>
              </Box>
              <Box className="flex flex-col gap-2">
                <Typography variant="body2" fontWeight={400} fontSize={14}>
                  Status
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  fontSize={14}
                  sx={{
                    textTransform: 'capitalize',
                    width: 'fit-content',
                    height: '24px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '8px',
                    padding: '4px 8px',
                    backgroundColor: bg,
                    color: color,
                  }}
                >
                  {value}
                </Typography>
              </Box>
              {user?.authorities === 'ROLE_SUPER_ADMIN' && (
                <Box className="flex flex-col gap-2">
                  <Typography variant="body2" fontWeight={400} fontSize={14}>
                    Channel Partner
                  </Typography>
                  <Typography variant="body2" fontWeight={600} fontSize={14}>
                    {subscription?.channelPartnerName ? subscription.channelPartnerName : 'N/A'}
                  </Typography>
                </Box>
              )}
              <Box className="flex flex-col gap-2">
                <Typography variant="body2" fontWeight={400} fontSize={14}>
                  Customer Name
                </Typography>
                <Typography variant="body2" fontWeight={600} fontSize={14}>
                  {subscription?.userName ? subscription.userName : 'N/A'}
                </Typography>
              </Box>
              <Box className="flex flex-col gap-2">
                <Typography variant="body2" fontWeight={400} fontSize={14}>
                  Product Type
                </Typography>
                <Typography variant="body2" fontWeight={600} fontSize={14}>
                  {subscription?.productItem ? subscription.productItem : 'N/A'}
                </Typography>
              </Box>
              <Box className="flex flex-col gap-2">
                <Typography variant="body2" fontWeight={400} fontSize={14}>
                  Subscription Type
                </Typography>
                <Typography variant="body2" fontWeight={600} fontSize={14}>
                  {subscription?.subscriptionType ? subscription.subscriptionType : 'N/A'}
                </Typography>
              </Box>
              <Box className="flex flex-col gap-2">
                <Typography variant="body2" fontWeight={400} fontSize={14}>
                  Quantity
                </Typography>
                <Typography variant="body2" fontWeight={600} fontSize={14}>
                  {subscription?.quantity ? subscription.quantity : 'N/A'}
                </Typography>
              </Box>
              <Box className="flex flex-col gap-2">
                <Typography variant="body2" fontWeight={400} fontSize={14}>
                  Valid Till
                </Typography>
                <Typography variant="body2" fontWeight={600} fontSize={14}>
                  {validTillDate ? formatDate(validTillDate) : 'N/A'}
                </Typography>
              </Box>
              <Box>
                <InputField
                  label="Total Payable Amount"
                  value={subscription?.totalPrice}
                  onChange={() => {}}
                  isDisabled={true}
                />
              </Box>
              <Box>
                <InputField
                  label="OutStanding Amount"
                  value={subscription?.totalOutstandingAmount}
                  onChange={() => {}}
                  isDisabled={true}
                />
              </Box>
            </Box>
            {subscription?.totalOutstandingAmount > 0 ? (
              <Box className="grid lg:grid-cols-3 md:grid-cols-3 grid-cols-2" sx={{ mt: 2, gap: 2 }}>
                <Box>
                  <InputField
                    label="Total Paid Amount"
                    placeholder="Total Paid Amount"
                    type="number"
                    value={formData.priceDue}
                    onChange={(e: any) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setFormData({ ...formData, priceDue: value });
                    }}
                  />
                </Box>
                <Box>
                  <InputField
                    label="Transaction Id"
                    placeholder="Transaction Id"
                    type="text"
                    value={formData.refrenceId}
                    onChange={handleReferenceIdChange}
                  />
                </Box>
                <Box>
                  <DateField
                    label="Transaction Date"
                    name="paymentDate"
                    value={formData.invoiceDate || ''}
                    onChange={(date) => setFormData({ ...formData, invoiceDate: date })}
                  />
                </Box>
              </Box>
            ) : (
              <Box className="flex justify-center items-center w-full" sx={{ mt: 2, gap: 2 }}>
                <Typography variant="body2" fontWeight={600} fontSize={18} color="green">
                  The full amount has been paid, no further payment is required at this time..
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{ mt: 8, mb: 4, justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
            className="grid grid-cols-2"
          >
            <Typography variant="h6" sx={{ color: (theme) => theme.palette.text.primary, fontWeight: 600 }}>
              Devices List
            </Typography>
            <CustomMultiSelectDropDown
              options={availableDevices}
              value={formData.selectedDevice}
              label="Search Device"
              onChange={(selectedValues: (string | number)[], isAllSelected?: boolean) => {
                const selectedStrings = selectedValues.map(String);
                const uniqueDevices = Array.from(new Set([...newSelectedDevices, ...selectedStrings]));
                setFormData((prev) => ({ ...prev, selectedDevice: selectedStrings }));
                setNewSelectedDevices(uniqueDevices);
              }}
              showDropdownArrow
              icon={true}
              selectAll={true}
            />
          </Box>
          <Box className="grid lg:grid-cols-2 md:grid-cols-2 grid-cols-1 mt-2" sx={{ mt: 2, gap: 2 }}>
            {newSelectedDevices.map((deviceId: string, index: number) => {
              return (
                <Box
                  key={`${deviceId}-${index}`}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                  className="relative"
                >
                  <CustomDropDownInput
                    label={`Device ${index + 1}`}
                    value={deviceId}
                    onChange={(value: string) => handleDeviceChange(value, index)}
                    options={filteredDevices.map((device: any) => ({
                      value: device.deviceId,
                      label: `${device.deviceId}`,
                    }))}
                    isDisabled={filteredDevices.length === 0}
                  />
                  <Box className="absolute right-0 top-[45%]">
                    <IconButton
                      onClick={() => {
                        const updatedDevices = [...newSelectedDevices];
                        updatedDevices.splice(index, 1);
                        setNewSelectedDevices(updatedDevices);
                      }}
                    >
                      <img src={removeIcon} alt="Delete" className="w-6 h-6" />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Box className="flex justify-between items-center w-full" sx={{ mt: 4 }}>
            <CustomIconButton text="Cancel" onClick={onClose} variant="outlined" background="transparent" />
            <CustomIconButton
              text="Save Changes"
              onClick={updateSubscription}
              variant="contained"
              isDisabled={!hasChanges}
            />
          </Box>
        </Box>
      </Modal>
      <Modal open={isEndSubscriptionModalOpen} onClose={() => setIsEndSubscriptionModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '440px',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 4,
          }}
        >
          <Typography variant="h6" sx={{ color: (theme) => theme.palette.text.primary }}>
            Delete Item
          </Typography>
          <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
            Are you sure you want to delete the item? This action cannot be undone.
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <CustomIconButton
              text="Cancel"
              onClick={() => setIsEndSubscriptionModalOpen(false)}
              variant="outlined"
              background="transparent"
            />
            <CustomIconButton
              text="Delete"
              onClick={handleEndSubscription}
              background="error"
              outline={false}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ModifySubscription;
